import {
    VisionClient,
    type VisionClientConfig,
    type IEngineInitializer,
    BaseEngineInitializer,
} from '@akavirtual/deai-web-core';

/**
 * Manages the lifecycle of multiple VisionClient instances.
 * This allows for multiple independent 3D canvases in the application.
 */
export class VisionManager {
    private clients: Map<string, VisionClient> = new Map();

    /**
     * Creates and registers a new VisionClient instance using a specific initializer.
     * @param instanceId A unique identifier for the engine instance.
     * @param config The configuration for the VisionClient.
     * @param initializerClass The specific initializer class to use for this instance.
     * @returns The new or existing VisionClient instance.
     */
    public createClient(
        instanceId: string,
        config: VisionClientConfig,
        // We accept the class as an argument, defaulting to a base initializer.
        initializerClass: new () => IEngineInitializer = BaseEngineInitializer
    ): VisionClient {
        if (this.clients.has(instanceId)) {
            return this.clients.get(instanceId)!;
        }

        // Instantiate the *provided* initializer class
        const initializer = new initializerClass();
        const client = new VisionClient(instanceId, initializer, config);
        this.clients.set(instanceId, client);
        console.log(`[VisionManager] Created client: ${instanceId}`);
        return client;
    }

    /**
     * Retrieves a client by its ID.
     * @param instanceId The ID of the client to retrieve.
     * @returns The VisionClient instance or undefined if not found.
     */
    public getClient(instanceId: string): VisionClient | undefined {
        return this.clients.get(instanceId);
    }

    /**
     * Destroys and unregisters a VisionClient instance.
     * This is crucial for cleanup when a canvas component unmounts.
     * @param instanceId The ID of the client to destroy.
     */
    public destroyClient(instanceId: string): void {
        const client = this.clients.get(instanceId);
        if (client) {
            client.destroy();
            this.clients.delete(instanceId);
            console.log(`[VisionManager] Destroyed client: ${instanceId}`);
        }
    }
}